import React, { useState, useEffect, useRef, useCallback } from "react";
import EmojiPicker from "emoji-picker-react";
// import { Image } from "antd";
import {
  formatDate,
  formatTime,
  isMessageFromToday,
  isNewDay,
  isoDateFormat,
  isToday,
  isYesterday,
} from "../../utils/Utils";
import { useDispatch, useSelector } from "react-redux";
import { CONSTANTS } from "../../utils/Constant/Constant";
import ToastNotification from "../../components/Common/ToastNotification";
import { uploadFileMessengerThunk } from "../../redux/Slices/uploadFileMessengerSlice";
// import AudioPlayerWithControls from "../../components/AudioPlayerWithControls";
import { Spinner } from "react-bootstrap";
import useCustomTranslation from "../../hooks/useCustomTranslation";
// import { updateCheckInReadStatusAPI } from "../../api/clientCheckInAPI";
// import ReactPlayer from "react-player";
import CheckInDetailsMessenger from "../Messenger/CheckInDetailsMessenger";
import {
  AudioWithOnClickFetch,
  ImageWithOnClickFetch,
  PDFWithOnClickFetch,
  VideoWithOnClickFetch,
} from "./MediaFilesController";
import { fetchSassToken } from "../../redux/Slices/toolboxExerciseSlice";
import { uploadVideoInChunks } from "./uploadVideoWithSAS";

const ChatArea = ({
  selectedUser,
  conversation,
  coachId,
  coachFriendlyName,
  setSelectedUser,
  callCheckInAPI,
}) => {
  const { trans } = useCustomTranslation();
  const dispatch = useDispatch();

  const { loading: imageLoader } = useSelector(
    (state) => state.uploadFileMessenger
  );

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [toastConfig, setToastConfig] = useState(null);
  const [lastReadIndex, setLastReadIndex] = useState(-1);
  const [lastReadByThemIndex, setlastReadByThemIndex] = useState(-1);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [paginator, setPaginator] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasReachedBeginning, setHasReachedBeginning] = useState(false);
  const [error, setError] = useState(null);
  // Delete message states
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [selectedMessageForDelete, setSelectedMessageForDelete] =
    useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCheckInDetails, setShowCheckInDetails] = useState(false);
  const [isParticipantOnline, setIsParticipantOnline] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [isVideoUploading, setIsVideoUploading] = useState(false);

  const emojiPickerRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageEndRef = useRef(null);
  const prevScrollHeight = useRef(0);

  const scrollToBottom = useCallback((behavior = "auto") => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ behavior });
    }
  }, []);

  const debounce = (func, wait) => {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  };
  // Only reset check-in details when client changes
  useEffect(() => {
    setShowCheckInDetails(false);
  }, [selectedUser?.participantId]);

  const handleMessageLongPress = (message) => {
    // Don't allow deletion of check-in messages
    try {
      const parsed = JSON.parse(message.text);
      if (parsed?.messageType === "CheckIn") return;
    } catch (e) {
      // Not a JSON message, can be deleted
    }

    setSelectedMessageForDelete(message);
    setShowDeleteModal(true);
  };

  const startLongPressTimer = (message) => {
    // Don't set timer for check-in messages
    try {
      const parsed = JSON.parse(message.text);
      if (parsed?.messageType === "CheckIn") return;
    } catch (e) {
      // Not a JSON message, can be deleted
    }

    const timer = setTimeout(() => {
      handleMessageLongPress(message);
    }, 800); // 800ms for long press
    setLongPressTimer(timer);
  };

  const cancelLongPress = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  const deleteMessage = async () => {
    if (!selectedMessageForDelete || !conversation) {
      setShowDeleteModal(false);
      return;
    }

    try {
      // Get all messages to find the one we want to delete
      const messagesPaginator = await conversation.getMessages();
      const messagesList = messagesPaginator.items;

      // Find the message by its index (sid)
      const messageToDelete = messagesList.find(
        (msg) => msg.index === selectedMessageForDelete.id
      );

      if (!messageToDelete) {
        throw new Error("Message not found");
      }

      // Remove the message
      await messageToDelete.remove();

      // Update local state
      setMessages((prev) =>
        prev.filter((m) => m.id !== selectedMessageForDelete.id)
      );

      setToastConfig({
        type: CONSTANTS.TOAST_SUCCESS,
        message: trans("Messenger.messageDeletedSuccessfully"),
        heading: CONSTANTS.TOAST_SUCCESS,
      });
    } catch (error) {
      setToastConfig({
        type: CONSTANTS.TOAST_ERROR,
        message: error.message || trans("Messenger.messageDeleteFailed"),
        heading: "Error",
      });
    } finally {
      setShowDeleteModal(false);
      setSelectedMessageForDelete(null);
    }
  };

  const fetchMessages = useCallback(
    async (direction = "newer") => {
      if (!conversation || loadingMoreMessages || loadingMessages) return;

      try {
        if (direction === "older") {
          setLoadingMoreMessages(true);
          prevScrollHeight.current =
            messagesContainerRef.current?.scrollHeight || 0;
        } else {
          setLoadingMessages(true);
        }

        let currentPaginator;
        let newMessages = [];

        if (direction === "older" && paginator?.hasPrevPage) {
          currentPaginator = await paginator.prevPage();

          newMessages = currentPaginator.items.map((msg) => ({
            id: msg.index,
            text: msg.body,
            sender: msg.author,
            date: msg.dateUpdated,
            time: formatTime(msg.dateCreated),
            status: msg.status || "sent",
          }));

          // Check if we've reached the beginning
          if (newMessages.length === 0) {
            setHasReachedBeginning(true);
            return;
          }
        } else {
          currentPaginator = await conversation.getMessages(100);
          newMessages = currentPaginator.items.map((msg) => ({
            id: msg.index,
            text: msg.body,
            sender: msg.author,
            date: msg.dateUpdated,
            time: formatTime(msg.dateCreated),
            status: msg.status || "sent",
          }));
        }

        setMessages((prev) => {
          // Filter out duplicates before adding
          const existingIds = new Set(prev.map((m) => m.id));
          const filteredNewMessages = newMessages.filter(
            (msg) => !existingIds.has(msg.id)
          );

          if (direction === "older") {
            return [...filteredNewMessages, ...prev];
          } else {
            return [...prev, ...filteredNewMessages];
          }
        });

        setPaginator(currentPaginator);
        setHasReachedBeginning(!currentPaginator.hasPrevPage);

        if (
          (isInitialLoad || direction === "newer") &&
          messagesContainerRef.current
        ) {
          setTimeout(() => {
            messageEndRef.current?.scrollIntoView({ behavior: "auto" });
            setIsInitialLoad(false);
          }, 100);
        }
      } catch (error) {
        setError("Failed to load messages. Please try again.");
        setTimeout(() => setError(null), 3000);
      } finally {
        setLoadingMoreMessages(false);
        setLoadingMessages(false);
      }
    },
    [
      conversation,
      paginator,
      loadingMoreMessages,
      loadingMessages,
      isInitialLoad,
    ]
  );

  // Update your scroll handler useEffect
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = debounce(() => {
      const { scrollTop } = container;
      const threshold = 100;

      if (
        scrollTop < threshold &&
        !loadingMoreMessages &&
        !loadingMessages &&
        !hasReachedBeginning &&
        !isInitialLoad
      ) {
        fetchMessages("older");
      }
    }, 200);

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [
    fetchMessages,
    loadingMoreMessages,
    loadingMessages,
    hasReachedBeginning,
    isInitialLoad,
  ]);

  useEffect(() => {
    if (!loadingMoreMessages && prevScrollHeight.current > 0) {
      const container = messagesContainerRef.current;
      if (container) {
        const newScrollHeight = container.scrollHeight;
        container.scrollTop = newScrollHeight - prevScrollHeight.current;
        prevScrollHeight.current = 0;
      }
    }
  }, [loadingMoreMessages, messages]);

  useEffect(() => {
    if (conversation) {
      setMessages([]);
      setPaginator(null);
      setHasReachedBeginning(false);
      setIsInitialLoad(true);
      fetchMessages("newer");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation]);

  useEffect(() => {
    setNewMessage("");

    if (!conversation) return;

    const listener = (message) => {
      setMessages((prev) => [
        ...prev,
        {
          id: message.index,
          text: message.body,
          sender: message.author,
          date: message.dateUpdated,
          time: formatTime(message.dateCreated),
          status: message.status || "sent",
        },
      ]);
      // Scroll to bottom when new message is received
      setTimeout(() => scrollToBottom("smooth"), 100);
    };

    const latestIndex = conversation?.lastReadMessageIndex;
    setLastReadIndex(latestIndex);

    conversation.on("messageAdded", listener);

    return () => {
      conversation.off("messageAdded", listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(e.target)
      ) {
        setShowEmojiPicker(false);
      }
    };
    if (showEmojiPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showEmojiPicker]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !conversation) return;
    try {
      await conversation.sendMessage(newMessage);
      setNewMessage("");
      // Scroll to bottom after sending message
      setTimeout(() => scrollToBottom("smooth"), 100);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };
  // Participant read status tracking
  useEffect(() => {
    const fetchParticipantLastRead = async () => {
      try {
        if (!conversation || !selectedUser?.friendlyName) return;

        const participant = await conversation.getParticipantByIdentity(
          selectedUser.friendlyName
        );
        //    AppState changes (AppState → send 'online' + 'lastOnline').
        const { online } = participant.attributes || {};

        let isOffline = false;
        if (online === false) {
          // Case 1: explicit flag from the user’s device
          isOffline = true;
        }

        if (participant) {
          // Check if participant is online
          setIsParticipantOnline(!isOffline);

          conversation.updateLastReadMessageIndex(
            participant.lastReadMessageIndex
          );

          setlastReadByThemIndex(participant.lastReadMessageIndex);
        }

        if (online === undefined) {
          setIsParticipantOnline(false);
        }
      } catch (error) {
        console.error("Failed to fetch participant's last read index:", error);
      }
    };

    const handleParticipantUpdate = (update) => {
      if (update?.participant?.identity === selectedUser?.friendlyName) {
        fetchParticipantLastRead();
      }
    };

    if (conversation && selectedUser?.friendlyName) {
      fetchParticipantLastRead();
      conversation.on("participantUpdated", handleParticipantUpdate);
    }

    return () => {
      conversation?.off("participantUpdated", handleParticipantUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation, selectedUser.friendlyName]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      const fileType = file.type;
      const fileName = file.name.toLowerCase();

      const isImage =
        fileType.startsWith("image/") ||
        /\.(jpg|jpeg|png|gif|bmp|webp)$/.test(fileName);
      const isVideo =
        fileType.startsWith("video/") ||
        /\.(mp4|webm|ogg|mov|avi|mkv)$/.test(fileName);
      const isAudio =
        fileType.startsWith("audio/") ||
        /\.(mp3|wav|aac|ogg|m4a)$/.test(fileName);
      const isPDF = fileType === "application/pdf" || /\.pdf$/.test(fileName);

      const isSupportedType = isImage || isVideo || isAudio || isPDF;

      if (!isSupportedType) {
        setToastConfig({
          type: CONSTANTS.TOAST_ERROR,
          message: `${trans("Messenger.unsupportedFileType")}`,
          heading: CONSTANTS.TOAST_ERROR,
        });
        setTimeout(() => {
          setToastConfig(null);
        }, CONSTANTS.TOAST_DURATION_SHORT);
        event.target.value = null;
        return;
      }

      // Set max size by type
      let maxSize = 10 * 1024 * 1024;
      let typeLabel = "File";

      if (isImage) {
        maxSize = 15 * 1024 * 1024;
        typeLabel = "Image";
      } else if (isVideo) {
        maxSize = 150 * 1024 * 1024;
        typeLabel = "Video";
      } else if (isAudio) {
        maxSize = 15 * 1024 * 1024;
        typeLabel = "Audio";
      } else if (isPDF) {
        maxSize = 15 * 1024 * 1024;
        typeLabel = "PDF";
      }

      if (file.size > maxSize) {
        setToastConfig({
          type: CONSTANTS.TOAST_ERROR,
          message: `${typeLabel} size cannot exceed ${
            maxSize / (1024 * 1024)
          } MB.`,
          heading: CONSTANTS.TOAST_ERROR,
        });
        setTimeout(() => {
          setToastConfig(null);
        }, CONSTANTS.TOAST_DURATION_SHORT);
        event.target.value = null;
        return;
      }
      // added timestamp to filename to make it unique
      const getTimestamp = () => {
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const dd = String(now.getDate()).padStart(2, "0");
        const hh = String(now.getHours()).padStart(2, "0");
        const min = String(now.getMinutes()).padStart(2, "0");
        const ss = String(now.getSeconds()).padStart(2, "0");

        return `${yyyy}${mm}${dd}_${hh}${min}${ss}`;
      };

      const timestamp = getTimestamp();
      const ext = file.name.substring(file.name.lastIndexOf("."));
      const base = file.name.substring(0, file.name.lastIndexOf("."));

      const newFileName = `${base}_${timestamp}${ext}`;

      const updatedFile = new File([file], newFileName, { type: file.type });

      setSelectedFile(updatedFile);
    }

    event.target.value = null;
  };

  const sendFileMessage = async () => {
    if (!selectedFile || !conversation) return;

    const isVideo = selectedFile.type.startsWith("video");

    try {
      if (isVideo) {
        setIsVideoUploading(true);
        setVideoUploadProgress(0);

        // Get SAS token
        const sasResponse = await dispatch(
          fetchSassToken({ fileName: selectedFile.name, isMessenger: true })
        ).unwrap();

        if (!sasResponse?.uploadUrl) {
          throw new Error("Failed to get SAS URL");
        }

        // Upload video in chunks
        const blobUrl = await uploadVideoInChunks({
          sasUrl: sasResponse.uploadUrl,
          file: selectedFile,
          onProgress: setVideoUploadProgress,
        });

        // Send chat message
        await conversation.sendMessage(
          JSON.stringify({
            fileName: selectedFile.name,
            mediaUrl: blobUrl,
            imageFileType: "video/mp4",
            messageType: "Messenger",
          })
        );

        setSelectedFile(null);
        return;
      }
      const toBase64 = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            const base64String = reader.result.split(",")[1];
            resolve(base64String);
          };
          reader.onerror = reject;
        });

      const base64String = await toBase64(selectedFile);

      const payload = {
        participantId: selectedUser?.participantId,
        conversationId: selectedUser?.conversationId,
        imageBase64: base64String,
        imageFileName: selectedFile.name,
        imageMediaType: selectedFile.type,
        imageFileType: selectedFile.type.split("/")[1],
        createdBy: coachId,
      };

      dispatch(uploadFileMessengerThunk(payload)).then((data) => {
        if (data?.payload?.success) {
          conversation.sendMessage(
            JSON.stringify({
              fileName: selectedFile.name,
              mediaId: data?.payload?.mediaId,
              imageFileType: selectedFile.type.split("/")[1],
              mediaUrl: data?.payload?.mediaUrl,
              messageType: "Messenger",
            })
          );
          setSelectedFile(null);
        }
      });
    } catch (error) {
      console.error("Error sending file:", error);
    } finally {
      setIsVideoUploading(false);
    }
  };

  const handleCheckInClick = (message) => {
    // Parse the message content
    let parsedMessage = null;

    try {
      parsedMessage = JSON.parse(message.text);
      // const payload = {
      //   coachId: coachId,
      //   CheckInDetailsId: parsedMessage.checkInDetailsId,
      // };
      // parsedMessage?.checkInDetailsId && updateCheckInReadStatusAPI(payload);
    } catch (error) {
      console.error("Error parsing message:", error);
      return;
    }

    const checkInDate = isoDateFormat(parsedMessage?.msgDate);

    setSelectedUser((prev) => ({
      ...prev,
      selectedDateOnly: checkInDate,
      checkInId: parsedMessage?.checkInDetailsId,
    }));
    // Show the CheckInDetailsMessenger
    setShowCheckInDetails(true);
  };

  const handleEmojiSelect = (emojiData) => {
    setNewMessage((prev) => prev + emojiData.emoji);
  };

  const renderFilePreview = () => {
    if (!selectedFile) return null;

    const fileType = selectedFile.type;

    if (fileType.startsWith("image/")) {
      return (
        <img
          src={URL.createObjectURL(selectedFile)}
          alt="preview"
          className="w-24 h-24 object-cover rounded border"
        />
      );
    }

    if (fileType.startsWith("video/")) {
      return (
        <video controls className="w-32 h-24 rounded border">
          <source src={URL.createObjectURL(selectedFile)} type={fileType} />
          {trans("Messenger.unsupportedVideoMessage")}
        </video>
      );
    }

    if (fileType === "application/pdf") {
      return (
        <div className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded">
          <i className="bi bi-file-earmark-pdf text-red-600"></i>
          <span className="text-sm truncate max-w-[200px]">
            {selectedFile.name}
          </span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded">
        <span className="text-sm truncate max-w-[200px]">
          {selectedFile.name}
        </span>
      </div>
    );
  };

  // handel close check in details
  const handleCloseCheckInDetails = () => {
    setShowCheckInDetails(false);
  };
  return (
    <div className="flex-1 flex flex-row bg-white ml-1">
      <div className="flex-1 flex flex-col w-[70%]">
        {/* Header */}
        <div className="flex items-center p-1 pb-3 border-b shadow-sm bg-white">
          <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center text-lg font-bold text-white">
            {selectedUser?.name?.charAt(0).toUpperCase()}
          </div>
          <div className="ml-3">
            <h2 className="text-sm font-semibold">
              {selectedUser?.name?.charAt(0)?.toUpperCase() +
                selectedUser?.name?.slice(1)?.toLowerCase()}
            </h2>
            <span
              className={`text-xs ${
                isParticipantOnline ? "text-green-500" : "text-gray-500"
              }`}
            >
              {isParticipantOnline ? "Online" : "Offline"}
            </span>
          </div>
        </div>

        {/* Typing Indicator */}
        {/* {typingUser && <p>✍️ {typingUser} is typing...</p>} */}

        {/* Messages */}
        <div
          className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-100"
          ref={messagesContainerRef}
        >
          {loadingMoreMessages && (
            <div className="flex justify-center py-2">
              <Spinner animation="border" variant="primary" size="sm" />
            </div>
          )}

          {hasReachedBeginning && (
            <div className="text-center text-xs text-gray-500 py-2">
              {trans("Messenger.beginningOfConversation")}
            </div>
          )}

          {error && (
            <div className="text-red-500 text-center p-2 text-sm">{error}</div>
          )}

          {messages.length === 0 && !loadingMessages && (
            <div className="flex justify-center items-center h-full">
              <p>{trans("Messenger.noMessagesYet")}</p>
            </div>
          )}

          {loadingMessages ? (
            <div className="flex justify-center items-center h-full">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            messages.map((msg, index) => {
              const currentDate = new Date(msg.date);
              const previousMsg = messages[index - 1];
              const showDateSeparator =
                index === 0 ||
                isNewDay(currentDate, new Date(previousMsg?.date));
              const isMe = msg.sender === coachFriendlyName;

              let parsedMessage = null;
              let isCheckInMessage = false;

              try {
                parsedMessage = JSON.parse(msg.text);
                if (parsedMessage && parsedMessage.messageType === "CheckIn") {
                  isCheckInMessage = true;
                }
              } catch {}

              let parsedFile = null;
              if (!isCheckInMessage) {
                try {
                  const parsed = JSON.parse(msg.text);
                  if (parsed && (parsed.mediaId || parsed.fileName)) {
                    parsedFile = parsed;
                  }
                } catch {}
              }

              const isLastReadByMe = msg.id <= lastReadIndex;
              const isLastReadByThem = msg.id <= lastReadByThemIndex;
              const showReadReceipt =
                isMe && (isLastReadByThem || isLastReadByMe);

              return (
                <React.Fragment key={msg.id}>
                  {showDateSeparator && (
                    <div className="text-center my-4">
                      <span className="inline-block bg-white text-gray-500 text-xs px-3 py-1 rounded-full shadow">
                        {isToday(currentDate)
                          ? "Today"
                          : isYesterday(currentDate)
                          ? "Yesterday"
                          : currentDate.toDateString()}
                      </span>
                    </div>
                  )}

                  {isCheckInMessage ? (
                    <div className="flex my-2">
                      <div
                        className="relative max-w-[80%] px-4 py-3 rounded-lg text-sm shadow bg-white border border-blue-100 hover:bg-blue-50 cursor-pointer group"
                        onClick={() => handleCheckInClick(msg)}
                      >
                        <div className="flex items-center">
                          <i className="bi bi-calendar-check text-lg mr-4"></i>

                          {/* Content in the middle */}
                          <div className="flex-1">
                            <span className="font-medium  block">
                              Check-In Submitted
                            </span>
                            <div className="text-xs  mt-1">
                              {formatDate(new Date(parsedMessage.msgDate))}
                            </div>
                          </div>

                          {/* Right arrow - aligned right and vertically centered */}
                          <i className="bi bi-chevron-right fs-4 fw-bold group-hover:text-blue-500 ml-4"></i>
                        </div>

                        {/* Timestamp at bottom right */}
                        <span className="absolute bottom-1 right-2 text-[10px] text-gray-400">
                          {formatTime(currentDate)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`flex ${
                        isMe ? "justify-end" : "justify-start"
                      }`}
                    >
                      <div
                        className={`relative max-w-[80%] px-2 py-1 rounded-lg text-sm shadow ${
                          isMe
                            ? "bg-[#1769bb] text-white rounded-br-none"
                            : "bg-white text-gray-800 rounded-bl-none"
                        }`}
                        {...(isMe &&
                        !isCheckInMessage &&
                        isMessageFromToday(msg.date) &&
                        msg.id > lastReadByThemIndex
                          ? {
                              onTouchStart: () => startLongPressTimer(msg),
                              onTouchEnd: cancelLongPress,
                              onMouseDown: () => startLongPressTimer(msg),
                              onMouseUp: cancelLongPress,
                              onMouseLeave: cancelLongPress,
                            }
                          : {})}
                      >
                        {parsedFile ? (
                          <div className="flex flex-col bg-gray-40 p-2 rounded-md max-w-xs">
                            {[
                              "png",
                              "jpg",
                              "jpeg",
                              "gif",
                              "webp",
                              "image/jpeg",
                              "image/png",
                              "image/jpg",
                            ].includes(
                              parsedFile.imageFileType?.toLowerCase()
                            ) ? (
                              parsedFile?.mediaUrl ? (
                                // <Image
                                //   width={200}
                                //   height={180}
                                //   alt={parsedFile.fileName}
                                //   src={parsedFile?.mediaUrl}
                                // />
                                <ImageWithOnClickFetch
                                  parsedFile={parsedFile}
                                  width="200px"
                                  height="180px"
                                />
                              ) : (
                                <div className="flex items-center gap-2 p-2">
                                  <i className="bi bi-image text-lg"></i>
                                  <span>{parsedFile.fileName}</span>
                                </div>
                              )
                            ) : [
                                "mp4",
                                "webm",
                                "ogg",
                                "video/mp4",
                                "mov",
                                "video/quicktime",
                                "quicktime",
                              ].includes(
                                parsedFile.imageFileType?.toLowerCase()
                              ) ? (
                              parsedFile?.mediaUrl ? (
                                // <ReactPlayer
                                //   url={parsedFile?.mediaUrl}
                                //   controls
                                //   width="250px"
                                //   height="190px"
                                // />
                                <VideoWithOnClickFetch
                                  parsedFile={parsedFile}
                                  width="250px"
                                  height="190px"
                                />
                              ) : (
                                <div className="flex items-center gap-2 p-2">
                                  <i className="bi bi-camera-reels text-lg"></i>
                                  <span>{parsedFile.fileName}</span>
                                </div>
                              )
                            ) : [
                                "mp3",
                                "wav",
                                "ogg",
                                "audio/mp3",
                                "audio/wav",
                                "audio/ogg",
                                "audio",
                                "m4a",
                                "x-m4a",
                              ].includes(
                                parsedFile.imageFileType?.toLowerCase()
                              ) ? (
                              parsedFile?.mediaUrl ? (
                                <div className=" pl-2 pr-2 rounded-3 shadow">
                                  {/* <AudioPlayerWithControls
                                    blobUrl={parsedFile?.mediaUrl}
                                    fileName={parsedFile.fileName}
                                    isMe={isMe}
                                  /> */}
                                  <AudioWithOnClickFetch
                                    parsedFile={parsedFile}
                                  />
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 p-2">
                                  <i className="bi bi-mic text-lg"></i>
                                  <span>{parsedFile.fileName}</span>
                                </div>
                              )
                            ) : ["pdf"].includes(
                                parsedFile.imageFileType?.toLowerCase()
                              ) ? (
                              parsedFile?.mediaUrl ? (
                                // <a
                                //   href={parsedFile?.mediaUrl}
                                //   target="_blank"
                                //   rel="noopener noreferrer"
                                //   className={`${
                                //     isMe ? "text-white" : "text-primary"
                                //   } text-sm text-decoration-none flex items-center`}
                                // >
                                //   <i className="bi bi-file-earmark-pdf text-red-600 mr-1 fs-4" />
                                //   {parsedFile.fileName}
                                // </a>
                                <PDFWithOnClickFetch
                                  parsedFile={parsedFile}
                                  isMe={isMe}
                                />
                              ) : (
                                <div className="flex items-center gap-2 p-2">
                                  <i className="bi bi-file-earmark-pdf text-red-600"></i>
                                  <span>{parsedFile.fileName}</span>
                                </div>
                              )
                            ) : (
                              <div className="flex items-center gap-2 p-2">
                                <i className="bi bi-file-earmark text-lg"></i>
                                <span>{parsedFile.fileName}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="pb-0 mb-0">{msg.text}</p>
                        )}
                        <span
                          className={`block text-[10px] ${
                            isMe ? "text-white" : "text-gray-500"
                          } text-right`}
                        >
                          {formatTime(currentDate)}
                          {showReadReceipt ? (
                            <span className="text-gray-100">
                              {isLastReadByThem ? "  ✓✓" : "  ✓"}
                            </span>
                          ) : (
                            isMe && (
                              <span className="text-gray-100">{" ✓"}</span>
                            )
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })
          )}
          <div ref={messageEndRef} />
        </div>

        {/* Input */}
        <div className="input-bar shadow-sm flex items-center gap-2 p-2 bg-white border border-secondary rounded-lg">
          {/* Attachment Icons */}
          <div className="flex items-center gap-2">
            {/* PDF Picker (paperclip icon) */}
            <input
              type="file"
              accept="application/pdf"
              ref={(ref) => (window.pdfPicker = ref)}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <button
              onClick={() => window.pdfPicker && window.pdfPicker.click()}
              className="text-gray-500"
              title="Attach PDF"
            >
              <i className="bi bi-paperclip text-lg"></i>
            </button>

            {/* Image Picker */}
            <input
              type="file"
              accept=".png, .jpg, .jpeg"
              ref={(ref) => (window.imagePicker = ref)}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <button
              onClick={() => window.imagePicker && window.imagePicker.click()}
              className="text-gray-500"
              title="Attach Photo"
            >
              <i className="bi bi-image text-lg"></i>
            </button>

            {/* Video Picker */}
            <input
              type="file"
              accept="video/mp4"
              ref={(ref) => (window.videoPicker = ref)}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <button
              onClick={() => window.videoPicker && window.videoPicker.click()}
              className="text-gray-500"
              title="Attach Video"
            >
              <i className="bi bi-camera-reels text-lg"></i>
            </button>
          </div>

          <div className="flex-1 relative">
            {selectedFile ? (
              <div className="flex items-center gap-2">
                {renderFilePreview()}
                <button
                  onClick={() => setSelectedFile(null)}
                  className="text-red-500 text-sm"
                >
                  <i className="bi bi-x-square-fill text-red-600 fs-3"></i>
                </button>
              </div>
            ) : (
              <textarea
                rows={1} // single line initially
                className="w-full max-w-full border-none outline-none text-sm px-3 py-1 rounded resize-none overflow-y-hidden h-[40px]"
                value={newMessage}
                placeholder={trans("Messenger.typeMessage")}
                onChange={(e) => {
                  setNewMessage(e.target.value);

                  // reset first so it shrinks back if needed
                  e.target.style.height = "auto";
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                onKeyDown={(e) => {
                  // Enter = send, Shift+Enter = new line
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();

                    if (newMessage.trim() || selectedFile) {
                      selectedFile ? sendFileMessage() : sendMessage();
                    }

                    // reset to single line after send
                    e.target.style.height = "auto";
                  }
                }}
              />
            )}
          </div>

          <div className="relative" ref={emojiPickerRef}>
            <button
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="text-gray-500"
            >
              {!imageLoader && `😊`}
            </button>
            {showEmojiPicker && (
              <div className="absolute bottom-10 right-0 z-50">
                <EmojiPicker onEmojiClick={handleEmojiSelect} />
              </div>
            )}
          </div>

          <button
            onClick={selectedFile ? sendFileMessage : sendMessage}
            className={`relative text-blue-500 ${selectedFile ? "mr-12" : ""}`}
            disabled={imageLoader || isVideoUploading}
          >
            {isVideoUploading ? (
              // Video upload spinner with %
              <div className="relative flex items-center justify-center w-10 h-10">
                <Spinner animation="border" size="lg" variant="primary" />
                <span className="absolute text-[10px] font-semibold text-primary">
                  {videoUploadProgress}%
                </span>
              </div>
            ) : imageLoader ? (
              // Image / PDF / Audio loader
              <Spinner animation="border" size="lg" variant="primary" />
            ) : (
              // Normal send icon
              <i className="bi bi-send-fill text-lg"></i>
            )}
          </button>
        </div>
        {/* Delete confirmation modal inside the drawer */}
        {showDeleteModal && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold mb-4">Delete Message</h3>
              <p className="mb-6">
                {trans("Messenger.deleteMessageConfirmation")}
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition"
                >
                  {trans("Common.cancel")}
                </button>
                <button
                  onClick={deleteMessage}
                  className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition"
                >
                  {trans("Common.delete")}
                </button>
              </div>
            </div>
          </div>
        )}
        {toastConfig && (
          <ToastNotification
            type={toastConfig.type}
            message={toastConfig.message}
            heading={toastConfig.heading}
          />
        )}
      </div>

      {showCheckInDetails && (
        <div className="w-[30%] border-l flex flex-col">
          <CheckInDetailsMessenger
            selectedUser={selectedUser}
            // key={selectedUser.selectedDateOnly}
            onClose={handleCloseCheckInDetails}
            callCheckInAPI={callCheckInAPI}
          />
        </div>
      )}
    </div>
  );
};

export default ChatArea;
